import { useState } from "react";
import { Play } from "lucide-react";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { Reveal } from "@/components/site/Reveal";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import type { LandingVideo } from "@/content/landing/media";
import { VideoSchema } from "@/components/site/VideoSchema";

type Props = {
  videos: LandingVideo[];
  trackingId: string;
  eyebrow?: string;
  title?: string;
  lede?: string;
};

/**
 * Lättviktsversion av PrimeVideoReel för SEO-landningssidor.
 * Klicka-för-att-spela poster + miniatyrer, YouTube-nocookie.
 */
export function LandingVideoReel({
  videos,
  trackingId,
  eyebrow = "Utvalda filmer",
  title = "Reel",
  lede = "Ett urval från vår YouTube-kanal — klicka för att spela.",
}: Props) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [playing, setPlaying] = useState(false);

  if (!videos || videos.length === 0) return null;
  const active = videos[activeIdx];

  return (
    <Section className="bg-surface/40">
      <VideoSchema videos={videos} />
      <EyebrowHeading eyebrow={eyebrow} title={<>{title}</>} lede={lede} />

      <Reveal>
        <div
          className="relative mt-10 aspect-video w-full overflow-hidden rounded-3xl border border-white/10 bg-black"
          style={{
            boxShadow: "0 30px 80px -20px color-mix(in oklab, var(--mint) 28%, transparent)",
          }}
        >
          {playing ? (
            <iframe
              key={active.videoId}
              src={`https://www.youtube-nocookie.com/embed/${active.videoId}?rel=0&modestbranding=1&autoplay=1`}
              title={active.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              className="absolute inset-0 h-full w-full"
              frameBorder={0}
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                setPlaying(true);
                track("landing_video_play", {
                  location: `landing_${trackingId}_reel`,
                  label: active.title,
                });
              }}
              className="group absolute inset-0 h-full w-full"
              aria-label={`Spela ${active.title}`}
            >
              <img
                src={`https://i.ytimg.com/vi/${active.videoId}/hqdefault.jpg`}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = `https://i.ytimg.com/vi/${active.videoId}/hqdefault.jpg`;
                }}
                alt={active.title}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span
                  className="flex h-20 w-20 items-center justify-center rounded-full bg-mint transition-transform duration-500 group-hover:scale-110"
                  style={{
                    boxShadow: "0 20px 50px -10px color-mix(in oklab, var(--mint) 55%, transparent)",
                  }}
                >
                  <Play className="h-7 w-7 fill-black text-black" />
                </span>
              </span>
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 md:p-7">
                <div>
                  {active.client && (
                    <div className="text-xs uppercase tracking-[0.18em] text-white/70">{active.client}</div>
                  )}
                  <div className="mt-1 text-base font-semibold text-white md:text-lg">{active.title}</div>
                </div>
              </div>
            </button>
          )}
        </div>
      </Reveal>

      {videos.length > 1 && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {videos.map((v, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={v.videoId}
                type="button"
                onClick={() => {
                  setActiveIdx(i);
                  setPlaying(false);
                }}
                className={cn(
                  "group relative aspect-video overflow-hidden rounded-xl border bg-black text-left transition-all hover:border-mint/40",
                  isActive ? "border-white/40" : "border-white/10 hover:-translate-y-0.5",
                )}
                aria-label={`Välj ${v.title}`}
                aria-pressed={isActive}
              >
                <img
                  src={`https://i.ytimg.com/vi/${v.videoId}/hqdefault.jpg`}
                  alt={v.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                {isActive && (
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-mint px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-black">
                    Spelas
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 p-3">
                  {v.client && (
                    <div className="text-[10px] uppercase tracking-[0.16em] text-mint">{v.client}</div>
                  )}
                  <div className="mt-0.5 line-clamp-2 text-xs font-medium text-white">{v.title}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Section>
  );
}
